import { z } from "zod";

/**
 * Stable, machine-readable API error codes — the shared vocabulary between the
 * api and the web app. Every HTTP error body carries `{ code, message }` (plus
 * the legacy lowercase `error` discriminator); the web switches on `code` to
 * show a real reason instead of a generic toast, and self-heals on
 * `AUTH_EXPIRED` (a full reload re-reads fresh Telegram initData).
 *
 * Codes are append-only: never rename or repurpose one.
 */
export const API_ERROR_CODES = [
  // ── Auth (401) ────────────────────────────────────────────────────────────
  /** initData is older than the accepted max age — a reload gets a fresh one. */
  "AUTH_EXPIRED",
  /** initData is missing / malformed / fails HMAC — reloading may not help. */
  "AUTH_INVALID",
  // ── Generic per-status fallbacks ──────────────────────────────────────────
  /** Malformed request (bad body/params); the zod/service message says why. */
  "VALIDATION",
  /** The requested app username is reserved (platform/staff/system handle). */
  "USERNAME_RESERVED",
  /** Authenticated but not allowed. */
  "FORBIDDEN",
  /** The addressed resource/user does not exist. */
  "NOT_FOUND",
  /** State conflict without a more specific social code. */
  "CONFLICT",
  /** Unexpected server error. */
  "INTERNAL",
  // ── Maintenance (503) ─────────────────────────────────────────────────────
  /**
   * The platform is in MAINTENANCE and the caller is not an admin. Sent with 503 and the
   * (admin-configurable) Persian message, so the web can render the maintenance screen
   * instead of a generic failure. Admins are exempt server-side — they keep full access so
   * they can test and turn maintenance back off.
   */
  "MAINTENANCE",
  // ── Account (403) ─────────────────────────────────────────────────────────
  /**
   * The caller's account is BANNED (`User.status = BANNED`). Enforced at the ONE
   * authenticate hook every mini-app route runs (and mirrored at every game-server
   * room join), so a banned user is locked out of the whole platform — the mini
   * app AND the games — not just the bot. Sent with 403 so the web can render a
   * clear "your account is banned" screen rather than a generic failure.
   */
  "BANNED",
  /**
   * The caller has not joined the platform's MANDATORY CHANNELS. Enforced at the same
   * authenticate hook (from the SAME `RequiredChannel` config the bot's join gate uses), so a
   * non-member cannot open/use the mini app — exactly as they cannot pass the bot's `/start`
   * gate. Sent with 403; the `details` carry the channels to join so the web can render the join
   * gate, and «عضو شدم» re-checks. Inactive by default (no channels configured → no gate).
   */
  "CHANNELS_REQUIRED",
  // ── Social graph (friends + follows) ──────────────────────────────────────
  /** The actor targeted themself (self-request / self-block / self-follow). */
  "SELF_ACTION",
  /** Already friends — e.g. reject/cancel on an accepted friendship. */
  "ALREADY_FRIENDS",
  /**
   * Reserved: a duplicate outgoing request. The server currently treats it as
   * an idempotent no-op success, so this code is not emitted today.
   */
  "ALREADY_PENDING",
  /**
   * A pending request exists but in the opposite direction for this action
   * (accept on your own outgoing request; cancel/reject on the wrong side).
   */
  "REVERSE_PENDING",
  /** No pending request exists to accept. */
  "NO_REQUEST",
  /** Not friends (yet) — e.g. remove on a merely-pending relationship. */
  "NOT_FRIENDS",
  /** A block (in either direction) forbids the action. */
  "BLOCKED",
  // ── Game invites ──────────────────────────────────────────────────────────
  /** The invite's 60s window elapsed (the row is flipped to EXPIRED lazily). */
  "INVITE_EXPIRED",
  /**
   * The invite already reached a DIFFERENT terminal state than this action
   * would produce (e.g. accept on a declined/canceled invite). Repeating the
   * SAME action (re-accept an accepted invite) is an idempotent success, not
   * this error.
   */
  "INVITE_NOT_PENDING",
  // ── Mandatory channels, no answer (503) ───────────────────────────────────
  /**
   * Telegram did not answer the channel-membership question within the time limit (or failed to
   * answer at all) and the caller is not a recently verified member (card-a5 item 5). Sent with 503
   * and the Persian retry sentence (`CHANNEL_CHECK_UNAVAILABLE_MESSAGE`): transient — asking again
   * is the remedy, and the web offers exactly that. Never «join the channels»: nothing said they
   * are not a member.
   */
  "CHANNEL_CHECK_UNAVAILABLE",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** The HTTP error body every api error resolves to. */
export const apiErrorBodySchema = z.object({
  /** Machine-readable code from {@link API_ERROR_CODES}. */
  code: z.enum(API_ERROR_CODES),
  /** Human-readable (English, developer-facing) explanation. */
  message: z.string(),
});
export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>;
